import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

// Services (you'll need to implement these)
import { getCustomerById, updateCustomer } from "../../services/Customer";

// Styled Components
const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
  padding: "10px 24px",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.shape.borderRadius * 1.5,
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.02),
    },
    "&.Mui-focused": {
      backgroundColor: theme.palette.background.paper,
    },
  },
}));

function UpdateCustomer() {
  const theme = useTheme();
  const history = useHistory();
  const { customerId } = useParams();

  // Customer state
  const [customer, setCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    customerName: "",
    phone: "",
    email: "",
    address: "",
    status: "",
  });

  // Validation errors
  const [errors, setErrors] = useState({});

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchCustomerData();
  }, [customerId]);

  const fetchCustomerData = async () => {
    setIsLoading(true);
    try {
      const result = await getCustomerById(customerId);

      if (result !== "Something went wrong...") {
        setCustomer(result);
        // Populate form with existing data
        setFormData({
          customerName: result.customerName || "",
          phone: result.phone || "",
          email: result.email || "",
          address: result.address || "",
          customerCode: result.customerCode || "",
          status: result.status || "INACTIVE",
        });
      } else {
        setError("Unable to fetch customer data!");
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
      setError("Unable to fetch customer data!");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    if (!formData.customerName.trim()) {
      newErrors.customerName = "Customer name is required";
    }

    if (formData.phone && !/^\+?[\d\s-()]+$/.test(formData.phone)) {
      newErrors.phone = "Invalid phone number format";
      console.log("Phone validation error");
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    console.log("Validation errors:", Object.keys(newErrors).length === 0);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: "Please fix the validation errors",
        severity: "error",
      });
      return;
    }
    console.log("Submitting form errors :", errors);

    setIsSaving(true);
    try {
      console.log("customerUpdate: ", formData);
      const result = await updateCustomer(customerId, formData);

      if (result && result !== "Something went wrong...") {
        setSnackbar({
          open: true,
          message: "Customer updated successfully!",
          severity: "success",
        });
        // Redirect to customer view after a short delay
        setTimeout(() => {
          history.push("/template/customer_view/" + customerId);
        }, 1500);
      } else {
        setSnackbar({
          open: true,
          message: "Failed to update customer. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error updating customer:", error);
      setSnackbar({
        open: true,
        message: "An error occurred while updating customer",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (customer) {
      setFormData({
        customerName: customer.customerName || "",
        phone: customer.phone || "",
        email: customer.email || "",
        address: customer.address || "",
        customerCode: customer.customerCode || "",
        status: customer.status || "INACTIVE",
      });
      setErrors({});
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">
          Loading customer details...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          m: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.error.light, 0.1),
        }}
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={() => history.goBack()}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 3 },
        m: { xs: 1, md: 3 },
        borderRadius: 2,
        bgcolor: "#f9fafb",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <EditIcon fontSize="large" color="primary" />
          <Typography variant="h5" fontWeight="600" color="primary.main">
            Edit Customer
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={() => history.goBack()}
            aria-label="close"
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.grey[500], 0.2),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Personal Information Section */}
          <Grid item xs={12}>
            <Card elevation={1} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <PersonIcon sx={{ mr: 1, color: "primary.main" }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="700"
                    color="primary.main"
                  >
                    Personal Information
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <StyledTextField
                      fullWidth
                      required
                      label="Customer Name"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleInputChange}
                      error={!!errors.customerName}
                      helperText={errors.customerName}
                      InputProps={{
                        startAdornment: (
                          <PersonIcon
                            sx={{
                              mr: 1,
                              color: "text.secondary",
                              fontSize: 20,
                            }}
                          />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <StyledTextField
                      fullWidth
                      label="Phone Number"
                      name="customerPhone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      error={!!errors.phone}
                      helperText={errors.phone}
                      InputProps={{
                        startAdornment: (
                          <PhoneIcon
                            sx={{
                              mr: 1,
                              color: "text.secondary",
                              fontSize: 20,
                            }}
                          />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <StyledTextField
                      fullWidth
                      label="Email Address"
                      name="customerEmail"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      InputProps={{
                        startAdornment: (
                          <EmailIcon
                            sx={{
                              mr: 1,
                              color: "text.secondary",
                              fontSize: 20,
                            }}
                          />
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <StyledTextField
                      fullWidth
                      label="Customer code"
                      name="customerCode"
                      value={formData.customerCode}
                      onChange={handleInputChange}
                      disabled={true}
                      InputProps={{
                        startAdornment: (
                          <BusinessIcon
                            sx={{
                              mr: 1,
                              color: "text.secondary",
                              fontSize: 20,
                            }}
                          />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.status === "ACTIVE"}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              status: e.target.checked ? "ACTIVE" : "INACTIVE",
                            })
                          }
                          name="status"
                        />
                      }
                      label={
                        formData.status === "ACTIVE" ? "Active" : "Inactive"
                      }
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Address Information Section */}
          <Grid item xs={12}>
            <Card elevation={1} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <LocationOnIcon sx={{ mr: 1, color: "primary.main" }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="700"
                    color="primary.main"
                  >
                    Address Information
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label="Street Address"
                      name="customerAddress"
                      value={formData.customerAddress}
                      onChange={handleInputChange}
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                pt: 2,
              }}
            >
              <ActionButton
                variant="outlined"
                color="secondary"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                disabled={isSaving}
              >
                Reset
              </ActionButton>
              <ActionButton
                variant="outlined"
                color="inherit"
                onClick={() => history.goBack()}
                disabled={isSaving}
              >
                Cancel
              </ActionButton>
              <ActionButton
                type="submit"
                variant="contained"
                color="primary"
                startIcon={
                  isSaving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </ActionButton>
            </Box>
          </Grid>
        </Grid>
      </form>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default UpdateCustomer;
